import { type PortalActions, type ReactAtom } from "@mongez/react-atom";
import { cn, useClickOutside } from "core/shared";
import { useEffect, useId, useRef } from "react";
import { twMerge } from "tailwind-merge";
import pattern from "~/assets/images/header/header-menu-pattern.png";
import { Button, Close } from "~/design-system/components";

export type PortalProps = {
  portal: ReactAtom<any, PortalActions>;
  width?: number;
  clickOutside?: boolean;
  title?: string;
  subtitle?: string;
  closeButton?: boolean;
  esc?: boolean;
  zIndex?: number;
  classes?: {
    root?: string;
    header?: {
      container?: string;
      title?: string;
      subtitle?: string;
    };
    overlay?: string;
    content?: string;
  };
  overlay?: boolean;
  children: React.ReactNode;
};

export function Portal({
  zIndex = 1000,
  portal,
  classes,
  children,
  clickOutside = true,
  closeButton = true,
  title,
  subtitle,
  width = 1000,
}: PortalProps) {
  const opened = portal.useOpened();
  const portalRef = useClickOutside(() => portal.close());
  const ref = useRef<HTMLDivElement>(null);

  const id = useId();

  useEffect(() => {
    if (opened && ref.current) {
      ref.current.focus();
    }
  }, [opened]);

  useEffect(() => {
    if (opened) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [opened]);

  if (!opened) return null;

  return (
    <div
      style={{
        zIndex,
      }}
      className={twMerge(
        "fixed inset-0 z-50 overflow-y-auto bg-black/70 p-6 backdrop-blur-lg md:p-10",
        classes?.root,
      )}
      id={`portal-${id}`}
      ref={ref as any}
    >
      <div className={cn("flex items-center justify-center min-h-[90%]", classes?.overlay)}>
        <div
          ref={clickOutside ? portalRef : undefined}
          className={cn(
            "animate-fade relative z-20 h-auto rounded-3xl bg-white p-6 md:rounded-[40px] md:p-10",
            classes?.content,
          )}
          style={{
            width: `${width}px`,
          }}
        >
          {/* Pattern */}
          <div className="from-primary-default/20 absolute inset-0 overflow-hidden bg-radial to-transparent">
            <img
              src={pattern}
              alt="pattern"
              className="z-0 h-full w-full object-cover opacity-50"
            />
          </div>
          {/* Header */}
          <div
            className={cn(
              "relative flex items-center justify-between pb-6",
              classes?.header?.container,
            )}
          >
            <div className="flex flex-col">
              <h2
                className={cn(
                  "text-lg font-bold sm:text-xl xl:text-2xl",
                  classes?.header?.title,
                )}
              >
                {title}
              </h2>
              <p
                className={cn(
                  "text-base font-medium text-gray-500",
                  classes?.header?.subtitle,
                )}
              >
                {subtitle}
              </p>
            </div>
            {closeButton && (
              <Button
                onClick={portal.close}
                className="size-12 rounded-full border bg-white/60 hover:bg-gray-100"
              >
                <Close />
              </Button>
            )}
          </div>
          {/* Content */}
          <div className="relative z-20 h-full">{children}</div>
        </div>
      </div>
    </div>
  );
}
